import { useMemo, useState } from "react";

import { useDispatch, useSelector } from "react-redux";

import Sentry from "@similarweb/sentry-logger";

import useActionAfterFlagChange from "app/pages/sales-intelligence/hooks/useActionAfterFlagChange";
import type { LeadsType } from "app/pages/sales-intelligence/modules/leads-search";
import {
    selectActiveLeadsType,
    selectSearchResultsDtoByLeadsType,
} from "app/pages/sales-intelligence/modules/leads-search";
import savedSearchesService from "app/pages/sales-intelligence/sub-modules/advanced-search/services/saved-searches/savedSearchesService";
import { saveSearchModalToggledAction } from "app/pages/sales-intelligence/sub-modules/advanced-search/store/action-creators";
import { selectSaveSearchModalPayload } from "app/pages/sales-intelligence/sub-modules/advanced-search/store/selectors";
import type { NewSearchDto } from "app/pages/sales-intelligence/sub-modules/advanced-search/types/dto";
import { selectProfileIsCreating } from "app/pages/sales-intelligence/sub-modules/contacts-search-profiles/store/selectors";
import type { ThunkDispatchCommon } from "single-spa/store/types";

import SaveSearchModal from "../../components/SaveSearchModal/SaveSearchModal";
import SaveSearchSuccessModal from "../../components/SaveSearchSuccessModal/SaveSearchSuccessModal";
import { useCreateSavedSearchMutation } from "../../mutations";

const SavedSearchModalContainer = () => {
    const dispatch: ThunkDispatchCommon = useDispatch();
    const { isOpen, saveMode } = useSelector(selectSaveSearchModalPayload);

    const viewModel = useMemo(() => savedSearchesService.initSaveSearchViewModel(saveMode)(), [saveMode]);

    const saveSearchMutation = useCreateSavedSearchMutation();
    const activeLeadsType: LeadsType = useSelector(selectActiveLeadsType);
    const dto = useSelector(selectSearchResultsDtoByLeadsType);

    const isProfileCreating = useSelector(selectProfileIsCreating);

    const [isSuccessModalOpen, setIsSuccessModalOpen] = useState(false);

    const handleSaveSubmit = async (name: string, isDefaultName: boolean) => {
        saveSearchMutation.mutate({
            name,
            ...dto[activeLeadsType],
        } as NewSearchDto);

        viewModel.onSubmitTracked?.(name, isDefaultName);
    };

    const handleSaveModalClose = () => {
        dispatch(saveSearchModalToggledAction({ isOpen: false, saveMode }));
    };

    const handleSaveCancel = () => {
        handleSaveModalClose();

        viewModel.onCancelTracked?.();
    };

    const handleSuccessModalSubmit = () => {
        setIsSuccessModalOpen(false);

        try {
            if (saveSearchMutation.isSuccess) {
                viewModel.onSuccess?.(saveSearchMutation.data.searchId);
            }
        } catch (e) {
            Sentry.error(e.message, e);
        }
    };

    useActionAfterFlagChange(
        saveSearchMutation.isPending,
        () => {
            handleSaveModalClose();
            setIsSuccessModalOpen(true);
        },
        !saveSearchMutation.isError,
    );

    return (
        <>
            <SaveSearchModal
                key={saveMode}
                isOpened={isOpen}
                isSubmitting={isProfileCreating || saveSearchMutation.isPending}
                textVariant={viewModel.i18nKeyModifier}
                defaultSearchName={viewModel.predefinedSearchName}
                onSubmit={handleSaveSubmit}
                onCancel={handleSaveCancel}
            />
            <SaveSearchSuccessModal
                isOpened={isSuccessModalOpen}
                onSubmitClick={handleSuccessModalSubmit}
            />
        </>
    );
};

export default SavedSearchModalContainer;
